// Client/src/services/SocketClient.ts
import { io, Socket } from 'socket.io-client';
import { sessionManager, SessionSnapshot, SessionStatus } from '../core/SessionManager';
import { getSocketTransportsForCurrentClient, isBackgroundSensitiveMobileClient } from '../utils/pushEnvironment';

// Browser-Kompatibilitätsprüfung
function checkSocketIOCompatibility(): void {
  const issues: string[] = [];
  
  if (typeof WebSocket === 'undefined') {
    issues.push('WebSocket nicht verfügbar');
  }
  
  if (typeof Promise === 'undefined') {
    issues.push('Promise nicht verfügbar');
  }
  
  if (issues.length > 0) {
    console.error('❌ Socket.IO Kompatibilitätsprobleme:', issues);
    console.warn('⚠️ Socket.IO könnte nicht funktionieren. Bitte Browser/Electron aktualisieren.');
  } else {
    console.log('✅ Socket.IO Kompatibilitätsprüfung bestanden');
  }
}

// Führe Prüfung beim Import aus
checkSocketIOCompatibility();

interface SocketClientConfig {
  employeeId?: string;
  token?: string;
  connectionType?: 'main' | 'admin' | 'native' | 'native-core';
  storeId?: string;
  clientFingerprint?: string;
}

interface SocketAuthErrorEvent {
  reason: string;
  message?: string;
  detailedError?: string;
  isTokenExpired?: boolean;
  employeeId?: string | null;
  connectionType?: string | null;
  timestamp?: string;
}

type SocketEventHandler<T = unknown> = {
  bivarianceHack(data: T): void;
}['bivarianceHack'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

class SocketClient {
  private socket: Socket | null = null;
  private config: SocketClientConfig | null = null;
  private isConnecting = false;
  private eventListeners: Map<string, Set<SocketEventHandler>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isOffline = false;
  private hasWindowListeners = false;
  private connectTimeout: ReturnType<typeof setTimeout> | null = null;
  private sessionToken: string | null = null;
  private sessionEmployeeId: string | null = null;
  private sessionStatus: SessionStatus = 'idle';
  private unsubscribeSession?: () => void;
  private isBackgroundSuspended = false;
  private readonly backgroundSensitiveClient = isBackgroundSensitiveMobileClient();

  private readonly handleOnlineEvent = () => {
    if (!this.config) {
      return;
    }
    if (this.isBackgroundSuspended) {
      return;
    }
    if (!this.isOffline && this.socket?.connected) {
      return;
    }
    this.isOffline = false;
    console.log('🌐 Online erkannt, versuche Reconnect...');
    this.attemptReconnect();
  };

  private readonly handleOfflineEvent = () => {
    if (this.isOffline) {
      return;
    }
    this.isOffline = true;
    console.log('🌐 Offline erkannt, pausiere Verbindungen');
    this.disconnect({
      preserveConfig: true,
      preserveEventListeners: true,
      keepWindowListeners: true
    });
  };

  private readonly handleVisibilityChange = () => {
    if (!this.backgroundSensitiveClient || !this.config) {
      return;
    }

    if (document.visibilityState === 'hidden') {
      if (this.isBackgroundSuspended) {
        return;
      }

      this.isBackgroundSuspended = true;
      console.log('📴 Mobile Hintergrundansicht erkannt, pausiere Socket-Verbindung.');
      this.disconnect({
        preserveConfig: true,
        preserveEventListeners: true,
        keepWindowListeners: true,
      });
      return;
    }

    if (!this.isBackgroundSuspended) {
      return;
    }

    this.isBackgroundSuspended = false;
    console.log('📳 Mobile Vordergrundansicht erkannt, stelle Socket-Verbindung wieder her.');
    this.attemptReconnect();
  };

  constructor() {
    // Jede Instanz ist unabhängig
    const snapshot = sessionManager.getSnapshot();
    this.sessionToken = snapshot.token;
    this.sessionEmployeeId = snapshot.user?.employee_id ?? null;
    this.sessionStatus = snapshot.status;
    this.unsubscribeSession = sessionManager.subscribe((state) => this.handleSessionUpdate(state));
  }

  // Verbindung herstellen - nur Employee ID und Token nötig
  async connect({ employeeId, token, connectionType = 'main', storeId, clientFingerprint }: SocketClientConfig): Promise<boolean> {
    const resolvedEmployeeId = employeeId ?? this.sessionEmployeeId ?? null;
    const resolvedToken = token ?? this.sessionToken ?? null;

    if (this.backgroundSensitiveClient && document.visibilityState === 'hidden') {
      this.isBackgroundSuspended = true;
      this.config = { employeeId: resolvedEmployeeId ?? undefined, connectionType, storeId, clientFingerprint };
      console.log(`📴 Socket-Verbindung fuer ${connectionType} im mobilen Hintergrund uebersprungen.`);
      return false;
    }

    // Prüfe Offline-Status
    if (this.isOffline || !navigator.onLine) {
      console.log(`🌐 Offline erkannt, pausiere Socket-Verbindung für Employee ${resolvedEmployeeId} (${connectionType})`);
      return false;
    }

    if (!resolvedEmployeeId || !resolvedToken) {
      console.warn('❌ Socket kann nicht verbunden werden: fehlende Authentifizierungsdaten', {
        employeeId: resolvedEmployeeId,
        tokenPresent: Boolean(resolvedToken),
      });
      this.isConnecting = false;
      return false;
    }

    // Wenn bereits verbunden mit derselben Config, sofort zurückgeben
    if (this.socket?.connected && this.config?.employeeId === resolvedEmployeeId && this.config?.connectionType === connectionType) {
      console.log(`✅ Socket bereits verbunden für Employee ${resolvedEmployeeId} (${connectionType})`);
      return true;
    }

    // Wenn bereits eine Verbindung besteht (anderer Typ), trennen
    if (this.socket?.connected) {
      console.log(`🔄 Trenne alte Verbindung (${this.config?.connectionType}) für neue Verbindung (${connectionType})`);
      this.disconnect();
    }

    // Warte, falls gerade eine Verbindung aufgebaut wird
    if (this.isConnecting) {
      console.log(`⏳ Warte auf laufenden Verbindungsaufbau...`);
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (this.socket?.connected) return true;
    }

    this.isConnecting = true;
    this.config = { employeeId: resolvedEmployeeId, connectionType, storeId, clientFingerprint };
    this.reconnectAttempts = 0;

    try {
      // Bestehende Verbindung sauber trennen
      if (this.socket) {
        this.socket.removeAllListeners();
        this.socket.disconnect();
      }

      console.log(`🔌 Verbinde Employee ${resolvedEmployeeId} (${connectionType})...`);

      // Verwende die aktuelle Window-Location für die Basis-URL
      const baseURL = `${window.location.protocol}//${window.location.host}`;
      console.log(`🔌 Socket.IO verbindet zu: ${baseURL}/api/socket.io`);

      // Für Entwicklung: Verwende Port 3000, da der Server dort läuft
      const serverURL = baseURL.includes('localhost') || baseURL.includes('127.0.0.1')
        ? `${window.location.protocol}//${window.location.hostname}:3000`
        : baseURL;

      console.log(`🔌 Socket.IO Server läuft auf: ${serverURL}`);
      
      // Einheitliche Auth-Methode für alle Clients (Browser & Electron)
      console.log('🔍 Verwende Auth-Objekt für Authentifizierung');
      const authPayload: Record<string, string> = {
        token: resolvedToken,
        employeeId: resolvedEmployeeId,
        connectionType
      };

      if (storeId) {
        authPayload.storeId = storeId;
      }

      if (clientFingerprint) {
        authPayload.clientFingerprint = clientFingerprint;
      }

      this.socket = io(`${serverURL}`, {
        path: '/api/socket.io',
        auth: authPayload,
        transports: getSocketTransportsForCurrentClient(),
        timeout: 20000,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        forceNew: true,
        autoConnect: true
      });

      return new Promise((resolve) => {
        this.clearConnectTimeout();
        this.connectTimeout = setTimeout(() => {
          console.error(`❌ Socket-Verbindung für Employee ${employeeId} ist abgelaufen`);
          this.isConnecting = false;
          this.socket = null;
          this.config = null;
          this.connectTimeout = null;
          resolve(false);
        }, 25000);

        this.socket!.on('connect', () => {
          this.clearConnectTimeout();
          console.log(`✅ Socket verbunden für Employee ${resolvedEmployeeId} (${connectionType})`);
          this.setupEventHandlers();
          this.isConnecting = false;
          this.isOffline = false;
          this.reconnectAttempts = 0;
          resolve(true);
        });

        this.socket!.on('connect_error', (error) => {
          this.clearConnectTimeout();
          console.error(`❌ Socket-Verbindungsfehler für Employee ${resolvedEmployeeId} (${connectionType}):`, error);
          this.isConnecting = false;

          if (this.handlePossibleAuthError(error)) {
            resolve(false);
            return;
          }

          const connectError: Record<string, unknown> = isRecord(error) ? error : {};
          const errorMessageText = typeof connectError.message === 'string' ? connectError.message : '';
          const isNetworkError =
            errorMessageText.includes('ENOTFOUND') ||
            errorMessageText.includes('ECONNREFUSED') ||
            errorMessageText.includes('Network Error') ||
            !navigator.onLine;

          if (isNetworkError) {
            console.log(`🌐 Netzwerkfehler erkannt, pausiere und teste alle 60 Sekunden`);
            this.isOffline = true;
            this.reconnectTimer = setTimeout(() => {
              this.isOffline = false;
              this.connect({ employeeId: resolvedEmployeeId, connectionType, storeId, clientFingerprint });
            }, 60000); // 60 Sekunden
          } else {
            if (this.reconnectAttempts < this.maxReconnectAttempts) {
              this.reconnectAttempts++;
              console.log(`🔄 Reconnect-Versuch ${this.reconnectAttempts}/${this.maxReconnectAttempts}...`);
              this.reconnectTimer = setTimeout(() => {
                this.connect({ employeeId: resolvedEmployeeId, connectionType, storeId, clientFingerprint });
              }, 2000 * this.reconnectAttempts);
            } else {
              this.socket = null;
              this.config = null;
            }
          }

          resolve(false);
        });

        this.socket!.on('connection_replaced', () => {
          this.clearConnectTimeout();
          console.log(`🔄 Verbindung für Employee ${resolvedEmployeeId} (${connectionType}) wurde durch neuere ersetzt`);
          this.disconnect();
        });
      });
    } catch (error) {
      console.error('❌ Fehler beim Verbinden:', error);
      this.clearConnectTimeout();
      this.isConnecting = false;
      this.socket = null;
      this.config = null;
      return false;
    }
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.addWindowListeners();

    // Universelle Event-Behandlung
    this.socket.on('disconnect', (reason) => {
      const type = this.config?.connectionType || 'unknown';
      console.log(`🔌 Socket getrennt für Employee ${this.config?.employeeId} (${type}): ${reason}`);
      if (reason === 'io server disconnect') {
        // Server hat Verbindung geschlossen - reconnect nach 1s
        setTimeout(() => this.attemptReconnect(), 1000);
      }
    });

    // Verbindungstyp-spezifische Events
    const connectionType = this.config?.connectionType;
    const isMonitoringConnection = connectionType === 'admin' || connectionType === 'native' || connectionType === 'native-core';

    if (isMonitoringConnection) {
      this.socket.on('admin_command_status', (data) => {
        this.emit('admin_command_status', data);
      });

      this.socket.on('native_client_status_update', (data) => {
        this.emit('native_status_update', data);
      });

      this.socket.on('native_client_heartbeat', (data) => {
        this.emit('native_client_heartbeat', data);
      });

      this.socket.on('native_client_connected', (data) => {
        this.emit('native_client_connected', data);
      });

      this.socket.on('native_client_disconnected', (data) => {
        this.emit('native_client_disconnected', data);
      });

      this.socket.on('native_client_stale_warning', (data) => {
        this.emit('native_client_stale_warning', data);
      });

      this.socket.on('native_log_event', (data) => {
        this.emit('native_log_event', data);
      });
    }

    // Native und Native-Core Event-Handler
    if (connectionType === 'native' || connectionType === 'native-core') {
      const isNativeCore = connectionType === 'native-core';
      
      // Command-Handler
      const commandEvent = isNativeCore ? 'admin_command_from_server' : 'admin_command_from_server';
      this.socket.on(commandEvent, (data) => {
        this.emit('admin_command_from_server', data);
      });

      // Heartbeat-Handler
      const heartbeatPingEvent = isNativeCore ? 'native_client_heartbeat' : 'native_client_ping';
      const heartbeatPongEvent = isNativeCore ? 'native_client_pong' : 'native_client_pong';
      
      this.socket.on(heartbeatPingEvent, (data) => {
        const payload = isRecord(data) ? data : {};
        const response = {
          heartbeatId: typeof payload.heartbeatId === 'string' ? payload.heartbeatId : undefined,
          respondedAt: new Date().toISOString()
        };
        if (this.socket?.connected) {
          this.socket.emit(heartbeatPongEvent, response);
        }
        this.emit('native_client_ping', data);
      });
    }

    if (connectionType === 'main') {
      this.socket.on('admin_command_status', (data) => {
        this.emit('admin_command_status', data);
      });
    }

    this.socket.on('auth_error', (payload) => {
      this.emit('auth_error', payload);
    });

    // Allgemeine App-Events - Server sendet mit Doppelpunkt-Notation
    // Diese Events sollten für ALLE Verbindungstypen empfangen werden
    this.socket.on('task:update', (data) => {
      this.emit('task_update', data); // Intern als task_update behandeln
    });

    this.socket.on('task:completed', (data) => {
      this.emit('task_update', data); // Als task_update behandeln
    });

    this.socket.on('notification', (data) => {
      this.emit('notification', data);
    });

    this.socket.on('sync:progress', (data) => {
      this.emit('sync:progress', data);
    });

    // Dashboard-spezifische Events
    this.socket.on('hmd:update', (data) => {
      this.emit('hmd:update', data);
    });

    this.socket.on('reinigung:update', (data) => {
      this.emit('reinigung:update', data);
    });

    this.socket.on('rolli:update', (data) => {
      this.emit('rolli:update', data);
    });

    this.socket.on('store:update', (data) => {
      this.emit('store:update', data);
    });

    this.socket.on('layout:update', (data) => {
      this.emit('layout:update', data);
    });

    this.socket.on('shelf:update', (data) => {
      this.emit('shelf:update', data);
    });

    this.socket.on('layout:historie', (data) => {
      this.emit('layout:historie', data);
    });

    this.socket.on('kampagne:update', (data) => {
      this.emit('kampagne:update', data);
    });

    this.socket.on('katalogeintrag:update', (data) => {
      this.emit('katalogeintrag:update', data);
    });

    this.socket.on('storeKatalog:update', (data) => {
      this.emit('storeKatalog:update', data);
    });

    this.socket.on('maintenance', (data) => {
      this.emit('maintenance', data);
    });
  }

  private attemptReconnect(): void {
    if (this.isBackgroundSuspended) {
      return;
    }

    if (this.config && !this.isConnecting) {
      console.log(`🔄 Versuche Reconnect für Employee ${this.config.employeeId} (${this.config.connectionType})...`);
      this.connect(this.config);
    }
  }

  // Einfache Event-API
  on<T = unknown>(event: string, callback: SocketEventHandler<T>): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback as SocketEventHandler);
  }

  off<T = unknown>(event: string, callback?: SocketEventHandler<T>): void {
    if (callback) {
      const listeners = this.eventListeners.get(event);
      if (listeners) {
        listeners.delete(callback as SocketEventHandler);
      }
    } else {
      this.eventListeners.delete(event);
    }
  }

  private emit(event: string, data: unknown): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`❌ Fehler im Event-Handler für ${event}:`, error);
        }
      });
    }
  }

  // Einfache Nachrichten-Sendung
  send(event: string, data: unknown): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn(`⚠️ Socket nicht verbunden - kann ${event} nicht senden`);
    }
  }

  // Verbindung sauber trennen
  disconnect(options: { preserveConfig?: boolean; preserveEventListeners?: boolean; keepWindowListeners?: boolean } = {}): void {
    const {
      preserveConfig = false,
      preserveEventListeners = false,
      keepWindowListeners = false
    } = options;

    const employeeId = this.config?.employeeId;
    const connectionType = this.config?.connectionType;

    // Stoppe laufende Reconnect-Versuche
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clearConnectTimeout();

    if (!keepWindowListeners) {
      this.removeWindowListeners();
    }

    if (this.socket) {
      console.log(`🔌 Trenne Socket für Employee ${employeeId || 'unknown'} (${connectionType || 'unknown'})`);
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    if (!preserveConfig) {
      this.config = null;
      this.isOffline = false;
    }

    this.isConnecting = false;
    this.reconnectAttempts = 0;
    if (!preserveEventListeners) {
      this.eventListeners.clear();
    }
  }

  private handleSessionUpdate(snapshot: SessionSnapshot) {
    const previousToken = this.sessionToken;
    const previousStatus = this.sessionStatus;

    this.sessionToken = snapshot.token;
    this.sessionEmployeeId = snapshot.user?.employee_id ?? null;
    this.sessionStatus = snapshot.status;

    const tokenChanged = previousToken !== this.sessionToken;
    const statusChanged = previousStatus !== this.sessionStatus;

    if (statusChanged && snapshot.status !== 'authenticated') {
      if (this.socket) {
        this.disconnect({
          preserveConfig: true,
          preserveEventListeners: true,
          keepWindowListeners: true,
        });
      }
      return;
    }

    if (tokenChanged && snapshot.status === 'authenticated' && this.config) {
      console.log('🔄 Session-Token aktualisiert, starte Reconnect des Socket-Clients');
      if (this.socket?.connected) {
        this.disconnect({
          preserveConfig: true,
          preserveEventListeners: true,
        });
      }
      this.attemptReconnect();
    }
  }

  // Status-Getter
  get isConnected(): boolean {
    return this.socket?.connected || false;
  }

  get currentEmployeeId(): string | null {
    return this.config?.employeeId || null;
  }

  get currentConnectionType(): string | null {
    return this.config?.connectionType || null;
  }

  // Zusätzliche Hilfsmethoden
  get connectionInfo(): unknown {
    return {
      isConnected: this.isConnected,
      employeeId: this.currentEmployeeId,
      connectionType: this.currentConnectionType,
      isConnecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  private clearConnectTimeout(): void {
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
  }

  private handlePossibleAuthError(error: unknown): boolean {
    const errorRecord = isRecord(error) ? error : {};
    const errorMessage = typeof errorRecord.message === 'string' ? errorRecord.message : '';
    const errorData = isRecord(errorRecord.data) ? errorRecord.data : {};
    const reason = typeof errorData.reason === 'string' ? errorData.reason : undefined;
    const detailedError = typeof errorData.error === 'string' ? errorData.error : '';
    const combined = `${errorMessage} ${detailedError}`.toLowerCase();

    const authReasons = new Set([
      'JWT_VERIFY_FAILED',
      'TOKEN_MISSING',
      'USER_NOT_FOUND',
      'EMPLOYEE_ID_MISSING'
    ]);

    const isAuthError =
      (reason && authReasons.has(reason)) ||
      combined.includes('unauthorized') ||
      combined.includes('nicht authentifiziert') ||
      combined.includes('ungültiges token');

    if (!isAuthError) {
      return false;
    }

    const isTokenExpired = combined.includes('jwt expired');

    console.warn('⚠️ Socket-Authentifizierungsfehler erkannt. Stoppe automatische Reconnects.', {
      reason,
      employeeId: this.config?.employeeId,
      connectionType: this.config?.connectionType,
      errorMessage,
      detailedError
    });

    const payload: SocketAuthErrorEvent = {
      reason: reason || 'AUTH_ERROR',
      message: errorMessage,
      detailedError,
      isTokenExpired,
      employeeId: this.config?.employeeId || null,
      connectionType: this.config?.connectionType || null,
      timestamp: new Date().toISOString()
    };

    this.emit('auth_error', payload);

    this.disconnect({
      preserveEventListeners: true
    });

    return true;
  }

  private addWindowListeners(): void {
    if (this.hasWindowListeners) {
      return;
    }
    window.addEventListener('online', this.handleOnlineEvent);
    window.addEventListener('offline', this.handleOfflineEvent);
    if (this.backgroundSensitiveClient) {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('pageshow', this.handleVisibilityChange);
    }
    this.hasWindowListeners = true;
  }

  private removeWindowListeners(): void {
    if (!this.hasWindowListeners) {
      return;
    }
    window.removeEventListener('online', this.handleOnlineEvent);
    window.removeEventListener('offline', this.handleOfflineEvent);
    if (this.backgroundSensitiveClient) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.removeEventListener('pageshow', this.handleVisibilityChange);
    }
    this.hasWindowListeners = false;
  }
}

export { SocketClient };
export type { SocketClientConfig, SocketAuthErrorEvent };
